<ion-header [translucent]="true">
  <ion-toolbar>
    <ion-buttons slot="start">
      <ion-button (click)="goBack()">
        <ion-icon name="arrow-back" slot="icon-only"></ion-icon>
      </ion-button>
    </ion-buttons>
    <ion-title>Insight Details</ion-title>
  </ion-toolbar>
</ion-header>

<ion-content [fullscreen]="true">
  <!-- Loading State -->
  <div *ngIf="isLoading" class="loading-container">
    <ion-spinner></ion-spinner>
    <p>Loading insight...</p>
  </div>

  <!-- Error State -->
  <div *ngIf="error && !isLoading" class="error-container">
    <ion-card color="danger">
      <ion-card-content>
        <p>{{ error }}</p>
        <ion-button fill="outline" (click)="loadInsight()" color="light">
          Try Again
        </ion-button>
      </ion-card-content>
    </ion-card>
  </div>

  <!-- Insight Details -->
  <div *ngIf="insight && !isLoading && !error" class="insight-details">
    <!-- Header Card -->
    <ion-card>
      <ion-card-header>
        <ion-card-title>{{ insight.category }}</ion-card-title>
        <ion-card-subtitle>
          Language: {{ insight.language }}
          <span *ngIf="insight.projectId">
            • Project ID: {{ insight.projectId }}</span
          >
        </ion-card-subtitle>
      </ion-card-header>
      <ion-card-content>
        <div class="stats-section">
          <h4>Statistics</h4>
          <div class="stats-grid">
            <div class="stat-item">
              <h3>{{ getUniqueConversationCount() }}</h3>
              <p>Conversations</p>
            </div>
            <div class="stat-item">
              <h3>{{ insight.topics?.length || 0 }}</h3>
              <p>Topics</p>
            </div>
            <div class="stat-item">
              <h3>{{ getAverageConfidence() * 100 | number: "1.0-0" }}%</h3>
              <p>Avg Confidence</p>
            </div>
          </div>
        </div>

        <div class="metadata-section">
          <ion-text color="medium">
            <small>
              Created {{ formatDateTime(insight.createdAt) }} • Updated
              {{ formatDateTime(insight.updatedAt) }}
            </small>
          </ion-text>
        </div>
      </ion-card-content>
    </ion-card>

    <!-- Related Recommendation -->
    <ion-card *ngIf="insight.recommendation">
      <ion-card-header>
        <ion-card-title>Related Recommendation</ion-card-title>
      </ion-card-header>
      <ion-card-content>
        <a [routerLink]="getRecommendationUrl(insight.recommendation.id)">
          <ion-item>
            <ion-label>
              <h3>{{ insight.category }}</h3>
              <div style="margin-top: 0.5rem">
                <ion-chip
                  [color]="
                    getRecommendationTypeColor(
                      insight.recommendation.recommendationType
                    )
                  "
                  size="small"
                >
                  <ion-icon
                    [name]="
                      getRecommendationTypeIcon(
                        insight.recommendation.recommendationType
                      )
                    "
                    size="small"
                  ></ion-icon>
                  {{
                    insight.recommendation.recommendationType === "create"
                      ? "Create New Article"
                      : "Update Existing Article"
                  }}
                </ion-chip>
                <ion-chip
                  *ngIf="insight.recommendation.ignoredAt"
                  color="medium"
                  size="small"
                  outline
                >
                  <ion-icon name="eye-off" size="small"></ion-icon>
                  Ignored
                </ion-chip>
              </div>
              <p *ngIf="insight.recommendation.existingArticleTitle">
                Existing article:
                {{ insight.recommendation.existingArticleTitle }}
              </p>
            </ion-label>
            <ion-icon name="chevron-forward" slot="end"></ion-icon>
          </ion-item>
        </a>
      </ion-card-content>
    </ion-card>

    <!-- Debug RAG Similarity -->
    <ion-card>
      <ion-card-header>
        <ion-card-title>Debug RAG Similarity</ion-card-title>
        <ion-card-subtitle
          >Analyze why this insight gets create vs update
          recommendations</ion-card-subtitle
        >
      </ion-card-header>
      <ion-card-content>
        <ion-button
          expand="block"
          fill="outline"
          [disabled]="debugLoading"
          (click)="debugRAGSimilarity()"
        >
          <ion-icon name="bug" slot="start"></ion-icon>
          <span *ngIf="!debugLoading">Run RAG Analysis</span>
          <span *ngIf="debugLoading">Analyzing...</span>
          <ion-spinner *ngIf="debugLoading" slot="end"></ion-spinner>
        </ion-button>

        <!-- Debug Error -->
        <ion-text color="danger" *ngIf="debugError">
          <p>{{ debugError }}</p>
        </ion-text>

        <!-- Debug Results -->
        <div *ngIf="ragDebugResult" class="debug-results">
          <!-- Search Query -->
          <ion-item>
            <ion-label>
              <h4>Search Query</h4>
              <p>{{ ragDebugResult.searchQuery }}</p>
            </ion-label>
          </ion-item>

          <!-- RAG Analysis Summary -->
          <ion-item>
            <ion-label>
              <h4>RAG Similarity Analysis</h4>
              <div class="rag-summary">
                <div class="rag-stat">
                  <strong>Articles Found:</strong>
                  {{ ragDebugResult.similarArticlesFound }}
                </div>
              </div>
            </ion-label>
          </ion-item>

          <!-- Similarity Scores -->
          <ion-item *ngIf="ragDebugResult.topScores.length > 0">
            <ion-label>
              <h4>Similarity Scores</h4>
              <ion-list>
                <ion-item *ngFor="let scoreResult of ragDebugResult.topScores">
                  <ion-label>
                    <h3>
                      <a
                        [href]="getArticleUrl(scoreResult.articleId)"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {{ scoreResult.articleTitle }}
                      </a>
                    </h3>
                    <p>Score: {{ scoreResult.score | number: "1.4-4" }}</p>
                    <p>Article ID: {{ scoreResult.articleId }}</p>
                  </ion-label>
                  <ion-chip color="primary" slot="end">
                    {{ scoreResult.score | number: "1.4-4" }}
                  </ion-chip>
                </ion-item>
              </ion-list>
            </ion-label>
          </ion-item>
        </div>
      </ion-card-content>
    </ion-card>

    <!-- Topics -->
    <ion-card *ngIf="insight.topics && insight.topics.length > 0">
      <ion-card-header>
        <ion-card-title>Topics ({{ insight.topics.length }})</ion-card-title>
      </ion-card-header>
      <ion-card-content>
        <ion-list>
          <a
            *ngFor="let topic of insight.topics"
            [routerLink]="getTopicUrl(topic.id)"
          >
            <ion-item>
              <ion-label>
                <h3>{{ topic.topicTitle }}</h3>
                <p>{{ topic.topicDescription }}</p>
                <div style="margin-top: 0.5rem">
                  <ion-text [color]="getConfidenceColor(topic.confidence)">
                    <small
                      >{{ topic.confidence * 100 | number: "1.0-0" }}%
                      confidence</small
                    >
                  </ion-text>
                  <span> • </span>
                  <ion-text color="medium">
                    <small
                      >Conversation ID:
                      {{ topic.conversationId.substring(0, 8) }}...</small
                    >
                  </ion-text>
                  <span> • </span>
                  <ion-text color="medium">
                    <small>{{ formatDate(topic.createdAt) }}</small>
                  </ion-text>
                </div>
              </ion-label>
              <ion-icon name="chevron-forward" slot="end"></ion-icon>
            </ion-item>
          </a>
        </ion-list>
      </ion-card-content>
    </ion-card>
  </div>
</ion-content>
